import { ROUTES } from "constants/routes"

/**
 * Build a URL with query parameters.
 *
 * @param {string} path - The base path.
 * @param {object} [params={}] - Key/value pairs to append as query string.
 * @returns {string} The full URL with query string.
 *
 * @example
 * buildUrl('/login', { redirect: '/dashboard' })
 * // => '/login?redirect=%2Fdashboard'
 */
export const buildUrl = (path, params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  if (entries.length === 0) return path
  const qs = new URLSearchParams(entries).toString()
  return `${path}?${qs}`
}

/**
 * Read a single query-string value from a URL search string.
 *
 * @param {string} search - The `location.search` string (e.g. `?redirect=/dashboard`).
 * @param {string} key - The query parameter name.
 * @returns {string|null}
 */
export const getQueryParam = (search, key) => {
  const params = new URLSearchParams(search)
  return params.get(key)
}

/**
 * Return the redirect destination after login.
 *
 * Reads the `redirect` query param; validates it is an internal path (starts
 * with `/` and contains no `://`) to prevent open-redirect attacks; falls back
 * to `/dashboard` if the param is absent, invalid, or points to a public path.
 *
 * @param {string} search - The `location.search` string.
 * @returns {string} A safe, internal redirect path.
 */
export const getLoginRedirect = search => {
  const redirect = getQueryParam(search, "redirect")
  const publicPaths = [
    ROUTES.LOGIN,
    ROUTES.SA_LOGIN,
    ROUTES.LOGOUT,
    ROUTES.FORGOT_PASSWORD,
  ]

  // Reject empty, external (contains ://), and non-absolute paths
  if (
    !redirect ||
    !redirect.startsWith("/") ||
    redirect.includes("://") ||
    publicPaths.includes(redirect)
  ) {
    return ROUTES.DASHBOARD
  }
  return redirect
}

/**
 * Check whether a route is a public (non-authenticated) route.
 *
 * @param {string} pathname - The current pathname.
 * @returns {boolean}
 */
export const isPublicRoute = pathname => {
  const publicPaths = [
    ROUTES.LANDING,
    ROUTES.LOGIN,
    ROUTES.SA_LOGIN,
    ROUTES.FORGOT_PASSWORD,
    ROUTES.VERIFY_PASSWORD,
    ROUTES.REGISTER,
    ROUTES.REGISTER_PLANS,
    ROUTES.REGISTER_PAYMENT,
    ROUTES.REGISTER_SUCCESS,
    ROUTES.DELETE_ACCOUNT,
    ROUTES.NOT_FOUND,
    ROUTES.SERVER_ERROR,
    ROUTES.FORBIDDEN,
    ROUTES.UNAUTHORIZED,
    ROUTES.SESSION_EXPIRED,
  ]
  // Also handle parameterised paths such as /reset-password/:uuid
  if (pathname.startsWith("/reset-password/")) return true
  return publicPaths.includes(pathname)
}

/**
 * Resolve breadcrumbs for a pathname from a route config array.
 *
 * @param {string} pathname - The current pathname.
 * @param {Array<object>} config - The routeConfig array.
 * @returns {Array<{label: string, path?: string}>}
 */
export const getBreadcrumbs = (pathname, config) => {
  const entry = config.find(r => r.path === pathname)
  return entry?.breadcrumbs || [{ label: "Dashboard", path: ROUTES.DASHBOARD }]
}

const DETAIL_OR_FORM_SEGMENT =
  /^(add|edit|create|view|checkout|add-.+|edit-.+|.*-details?|details|edit-item)$/i

/** Single-segment configuration / form routes (not list pages). */
const STANDALONE_FORM_PATHS = new Set([
  "/referral-settings",
  "/loyalty-settings",
  "/order-cancel-settings",
  "/giftcard-settings",
  "/add-emails",
  "/change-password",
  "/my-preference",
  "/generate-qr-code",
  "/generate-bulk-coupons",
  "/add-digital-coupons",
])

/** Paths where the parent segment alone is not the list route */
const BACK_FALLBACK_OVERRIDES = {
  "/customers/deleted-customer-details": "/customers/deleted-customers",
  "/superadmin/users/deleted-users/user-details": "/superadmin/users/deleted-users",
  "/menu/menu-setup/view": "/menus/menu-setup",
  "/menu/menu-setup/edit": "/menus/menu-setup",
  "/referral-settings": "/app-settings",
  "/loyalty-settings": "/app-settings",
  "/order-cancel-settings": "/app-settings",
  "/giftcard-settings": "/app-settings",
  "/settings/social-auth": "/settings/integrations",
  "/change-password": ROUTES.DASHBOARD,
  "/my-preference": ROUTES.DASHBOARD,
  "/generate-qr-code": ROUTES.DASHBOARD,
  "/generate-bulk-coupons": "/coupons-group",
  "/add-digital-coupons": "/coupons-group",
}

/**
 * Default dashboard for the current session (tenant vs platform).
 */
export const getDefaultDashboardPath = pathname => {
  if (pathname.startsWith("/superadmin")) {
    return ROUTES.SA_DASHBOARD
  }
  return ROUTES.DASHBOARD
}

/**
 * Infer the list (parent) route for detail / add / edit pages.
 * Used when browser history has no prior entry (e.g. after login replace).
 */
export const getBackFallbackPath = pathname => {
  const normalized = (pathname || "").replace(/\/+$/, "") || "/"
  if (BACK_FALLBACK_OVERRIDES[normalized]) {
    return BACK_FALLBACK_OVERRIDES[normalized]
  }

  // Menu item edit: .../categories/:id/items/:itemId/edit-item -> items list
  const menuItemEditMatch = normalized.match(
    /^(\/menus\/categories\/[^/]+\/items)\/[^/]+\/edit-item$/
  )
  if (menuItemEditMatch) {
    return menuItemEditMatch[1]
  }

  // Menu item add under category: .../items/add -> items list
  const menuCategoryItemAddMatch = normalized.match(
    /^(\/menus\/categories\/[^/]+\/items)\/add$/
  )
  if (menuCategoryItemAddMatch) {
    return menuCategoryItemAddMatch[1]
  }

  if (normalized === "/menus/items/add") {
    return "/menus/items"
  }

  // Banner slide add/edit: nested under banner id but list lives at module root
  const bannerSlideMatch = normalized.match(
    /^(\/(?:offer-)?banners)\/[^/]+\/slides\/(?:add|edit)$/
  )
  if (bannerSlideMatch) {
    return bannerSlideMatch[1]
  }

  if (normalized === "/menus/modifier-group-details") {
    return "/menus/modifier-groups"
  }

  const segments = normalized.split("/").filter(Boolean)
  if (segments.length === 0) {
    return getDefaultDashboardPath(normalized)
  }

  const last = segments[segments.length - 1]
  if (DETAIL_OR_FORM_SEGMENT.test(last) || last.includes("detail")) {
    return `/${segments.slice(0, -1).join("/")}`
  }

  if (segments.length === 1) {
    return getDefaultDashboardPath(normalized)
  }

  return `/${segments.slice(0, -1).join("/")}`
}

/**
 * Whether the breadcrumb Back control should show (super-admin console only).
 */
export const shouldShowBackButton = pathname => {
  const normalized = (pathname || "").replace(/\/+$/, "") || "/"

  if (!normalized.startsWith("/superadmin")) {
    return false
  }

  if (normalized === ROUTES.SA_DASHBOARD) {
    return false
  }

  if (STANDALONE_FORM_PATHS.has(normalized)) {
    return true
  }

  const segments = normalized.split("/").filter(Boolean)
  if (segments.length === 0) {
    return false
  }

  const last = segments[segments.length - 1]
  if (DETAIL_OR_FORM_SEGMENT.test(last) || last.includes("detail")) {
    return true
  }

  return false
}
